import React from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { LicenseProvider } from "./context/licenseContext.js";
import { LocationProvider } from "./context/locationContext";
import { AppStateProvider, useAppState } from "./context/appStateContext";
import { CoaJobsProvider } from "./context/coaJobsContext";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import Loading from "./components/Loading/Loading";
import ProtectedLayout from "./components/Layout/ProtectedLayout";
import LoginOverlay from "./components/LoginOverlay/LoginOverlay";
import AccessDenied from "./components/AccessDenied/AccessDenied";
import RegistrationPage from "./Pages/RegistrationPage/registrationPage";
import Dashboard from "./Pages/Dashboard/Dashboard";
import COA from "./Pages/COA/COA.js";
import Showroom from "./Pages/Showroom/showroom";
import Profile from "./Pages/Settings/Profile/Profile.js";
import SettingsLayout from "./Pages/Settings/SettingsLayout";
import SettingsRedirect from "./Pages/Settings/SettingsRedirect";
import SettingsGuard from "./Pages/Settings/SettingsGuard";
import UserProfile from "./Pages/Settings/UserProfile/UserProfile.js";
import UserDetails from "./components/Admin/user/userDetails";
import AdminPanel from "./Pages/AdminPanel/AdminPanel";
import Connections from "./components/Connections/Connections";
import NotFoundPage from "./Pages/NotFoundPage/NotFound.js";
import OrderSuccess from "./Pages/OrderSuccess/OrderSuccess.js";
import CompoundEducation from "./Pages/CompoundEducation/CompoundEducation.js";
import PublicCompliance from "./Pages/PublicCompliance/PublicCompliance";
import ReportPage from "./Pages/PublicCompliance/ReportPage";
import GeneralReportPage from "./Pages/PublicCompliance/GeneralReportPage";
import ProductInfoPage from "./Pages/PublicCompliance/ProductInfoPage";
import MixInfoPage from "./Pages/PublicCompliance/MixInfoPage";
import LeoPage from "./Pages/PublicCompliance/LeoPage";
import MixDetail from "./Pages/MixDetail/MixDetail";
import InvoiceReview from "./Pages/InvoiceReview/InvoiceReview";
import ComplianceDocuments from "./Pages/ComplianceDocuments/ComplianceDocuments";
import CRMPanel from "./Pages/CRMPanel/CRMPanel";
import CRMInbound from "./Pages/CRMPanel/CRMInbound";
import SalesAnalytics from "./Pages/SalesAnalytics/SalesAnalytics.js";
import Orders from "./Pages/Orders/Orders";
import LabOrders from "./Pages/LabOrders/LabOrders";
import "./App.css";

const CompanyProfile = React.lazy(() => import("./Pages/Settings/sections/company/CompanyProfile"));
const EmployeeManagement = React.lazy(() => import("./Pages/Settings/sections/company/EmployeeManagement"));
const SettingsLocations = React.lazy(() => import("./Pages/Settings/sections/company/Locations"));
const SettingsSupplyChain = React.lazy(() => import("./Pages/Settings/sections/company/SupplyChain"));
const SettingsPayments = React.lazy(() => import("./Pages/Settings/sections/company/PaymentSettings"));
const SettingsPricing = React.lazy(() => import("./Pages/Settings/sections/company/PricingTiers"));
const AccountProfile = React.lazy(() => import("./Pages/Settings/sections/account/AccountProfile"));
const Preferences = React.lazy(() => import("./Pages/Settings/sections/account/Preferences"));
const Faq = React.lazy(() => import("./Pages/Settings/sections/help/Faq"));
const Tutorials = React.lazy(() => import("./Pages/Settings/sections/help/Tutorials"));

// --- Route wrapper components (bridge context → props) ---

function RootLayout() {
    const { sessionLoading } = useAppState();
    if (sessionLoading) return <Loading />;
    return <Outlet />;
}

function LoginWrapper() {
    const { user, completeLogin } = useAppState();
    if (user) return <Navigate to="/Dashboard" replace />;
    return <LoginOverlay onLoginSuccess={completeLogin} />;
}

function DashboardWrapper() {
    const { userId, userProfile, company, given_name, licenseNo, setSessionLoading, title, profilePicUrl, setCOAProcessing, setHeaderTitle } = useAppState();
    return (
        <Dashboard
            userId={userId}
            userProfile={userProfile}
            company={company}
            givenName={given_name}
            licenseNo={licenseNo}
            setSessionLoading={setSessionLoading}
            title={title}
            profilePicUrl={profilePicUrl}
            setCOAProcessing={setCOAProcessing}
            setHeaderTitle={setHeaderTitle}
        />
    );
}

function SettingsLayoutWrapper() {
    const { setHeaderAndMenu, setHeaderTitle } = useAppState();
    return <SettingsLayout setHeaderAndMenu={setHeaderAndMenu} setHeaderTitle={setHeaderTitle} />;
}

function ShowroomWrapper() {
    const { userId, setHeaderTitle } = useAppState();
    return <Showroom userId={userId} setHeaderTitle={setHeaderTitle} />;
}

function AdminPanelWrapper() {
    const { userId, userProfile, setHeaderTitle } = useAppState();
    return <AdminPanel userId={userId} userProfile={userProfile} setHeaderTitle={setHeaderTitle} />;
}

function AdminRoute() {
    const { userProfile } = useAppState();
    if (userProfile !== "admin" && userProfile !== "superadmin") return <AccessDenied />;
    return <Outlet />;
}

function UserDetailsWrapper() {
    const { userId, userProfile, company, given_name, licenseNo, profilePicUrl, setHeaderTitle, title } = useAppState();
    return (
        <UserDetails
            userId={userId}
            userProfile={userProfile}
            company={company}
            givenName={given_name}
            licenseNo={licenseNo}
            profilePicUrl={profilePicUrl}
            setHeaderTitle={setHeaderTitle} 
            title={title}
        />
    );
}

function ProfileWrapper() {
    const { userId, company, given_name, phone_number, email, familyName, licenseNo, updated, setUpdated, profilePicUrl, setHeaderTitle } = useAppState();
    return (
        <Profile
            userId={userId}
            company={company}
            givenName={given_name}
            phone_number={phone_number}
            email={email}
            familyName={familyName}
            licenseNo={licenseNo}
            update={updated}
            setUpdate={setUpdated}
            profilePicUrl={profilePicUrl}
            setHeaderTitle={setHeaderTitle}
        />
    );
}

function ConnectionsWrapper() {
    const { userId, userProfile, company, given_name, phone_number, email, familyName, licenseNo, updated, setUpdated, profilePicUrl, setHeaderTitle, title } = useAppState();
    return (
        <Connections
            userId={userId}
            userProfile={userProfile}
            company={company}
            givenName={given_name}
            phone_number={phone_number}
            email={email}
            familyName={familyName}
            licenseNo={licenseNo}
            update={updated}
            setUpdate={setUpdated}
            profilePicUrl={profilePicUrl} 
            setHeaderTitle={setHeaderTitle}
            title={title}
        />
    );
}
 
function UserProfileWrapper() {
    const { userId, userProfile, company, given_name, phone_number, email, familyName, licenseNo, updated, setUpdated, profilePicUrl, setHeaderTitle, title, isContainerVisible, setIsContainerVisible, channelUrl, setChannelUrl } = useAppState();
    return ( 
        <UserProfile 
            userId={userId}
            userProfile={userProfile}
            company={company} 
            givenName={given_name}
            phone_number={phone_number}
            email={email}
            familyName={familyName}
            licenseNo={licenseNo}
            update={updated}
            setUpdate={setUpdated}
            profilePicUrl={profilePicUrl}
            setHeaderTitle={setHeaderTitle}
            title={title}
            isContainerVisible={isContainerVisible}
            setIsContainerVisible={setIsContainerVisible}
            channelUrl={channelUrl}
            setChannelUrl={setChannelUrl}
        />
    );
}

function COAWrapper() {
    const { userId, userProfile, company, given_name, setCOAProcessing, title, licenseNo, setHeaderTitle } = useAppState();
    return (
        <COA
            userId={userId}
            userProfile={userProfile}
            company={company}
            givenName={given_name}
            setCOAProcessing={setCOAProcessing}
            title={title}
            licenseNo={licenseNo}
            setHeaderTitle={setHeaderTitle}
        />
    );
}

function MixDetailWrapper() {
    const { setHeaderTitle } = useAppState();
    return <MixDetail setHeaderTitle={setHeaderTitle} />;
}

function ComplianceDocumentsWrapper() {
    const { userId, setHeaderTitle } = useAppState();
    return <ComplianceDocuments userId={userId} setHeaderTitle={setHeaderTitle} />;
}

function CompoundWrapper() {
    const { setHeaderTitle } = useAppState();
    return <CompoundEducation setHeaderTitle={setHeaderTitle} />;
}

function OrderSuccessWrapper() {
    return <OrderSuccess />;
}

function CRMPanelWrapper() {
    const { userId, userProfile, setHeaderTitle } = useAppState();
    return <CRMPanel userId={userId} userProfile={userProfile} setHeaderTitle={setHeaderTitle} />;
}

function CRMInboundWrapper() {
    const { userId, userProfile, setHeaderTitle } = useAppState();
    return <CRMInbound userId={userId} userProfile={userProfile} setHeaderTitle={setHeaderTitle} />;
}

function SalesAnalyticsWrapper() {
    const { userId, setHeaderTitle } = useAppState();
    return <SalesAnalytics userId={userId} setHeaderTitle={setHeaderTitle} />;
}

function OrdersWrapper() {
    const { setHeaderTitle } = useAppState();
    return <Orders setHeaderTitle={setHeaderTitle} />;
}

function LabOrdersWrapper() {
    const { userId, setHeaderTitle } = useAppState();
    return <LabOrders userId={userId} setHeaderTitle={setHeaderTitle} />;
}

// --- Router definition ---

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [  
            // Public routes (no menu/header)
            { path: "/", element: <LoginWrapper /> },
            { path: "/Register", element: <RegistrationPage /> },
            { path: "/compliance/report", element: <GeneralReportPage /> },
            { path: "/compliance/leo", element: <LeoPage /> },
            { path: "/compliance/:companySlug", element: <PublicCompliance /> },
            { path: "/compliance/:companySlug/report/:coaId", element: <ReportPage /> },
            { path: "/compliance/:companySlug/product/:coaId", element: <ProductInfoPage /> },
            { path: "/compliance/:companySlug/mix/:productId", element: <MixInfoPage /> },

            // Protected routes (with menu/header)
            {
                element: <ProtectedLayout />,
                children: [
                    { path: "/Dashboard", element: <DashboardWrapper /> },
                    { path: "/Settings", element: <Navigate to="/settings" replace /> },
                    {
                        path: "/settings",
                        element: <SettingsLayoutWrapper />,
                        children: [
                            { index: true, element: <SettingsRedirect /> },
                            { path: "company/profile", element: <SettingsGuard permKey="canSeeCompanyProfile"><CompanyProfile /></SettingsGuard> },
                            { path: "company/employees", element: <SettingsGuard permKey="canSeeEmployees"><EmployeeManagement /></SettingsGuard> },
                            { path: "company/locations", element: <SettingsGuard permKey="canSeeLocations"><SettingsLocations /></SettingsGuard> },
                            // Vendors merged into Supply Chain (2026-09-23) — keep old links working.
                            { path: "company/vendors", element: <Navigate to="/settings/company/supply-chain" replace /> },
                            { path: "company/supply-chain", element: <SettingsGuard permKey="canSeeSupplyChain"><SettingsSupplyChain /></SettingsGuard> },
                            { path: "company/payments", element: <SettingsGuard permKey="canSeePayments"><SettingsPayments /></SettingsGuard> },
                            { path: "company/pricing", element: <SettingsGuard permKey="canSeePricing"><SettingsPricing /></SettingsGuard> },
                            { path: "account/profile", element: <AccountProfile /> },
                            { path: "account/preferences", element: <Preferences /> },
                            { path: "help/faq", element: <Faq /> },
                            { path: "help/tutorials", element: <Tutorials /> },
                        ],
                    },
                    { path: "/showroom", element: <ShowroomWrapper /> },
                    { path: "/Profile/:id", element: <ProfileWrapper /> },
                    { path: "/Connections/:id", element: <ConnectionsWrapper /> },
                    { path: "/UserProfile/:id", element: <UserProfileWrapper /> },
                    { path: "/product/:coaId/:licenseId/:locationId?", element: <COAWrapper /> },
                    { path: "/mix/:productId/:licenseId", element: <MixDetailWrapper /> },
                    { path: "/compound/:slug", element: <CompoundWrapper /> },
                    { path: "/invoice-review/:invoiceId", element: <InvoiceReview /> },
                    { path: "/compliance-documents", element: <ComplianceDocumentsWrapper /> },
                    { path: "/orders", element: <OrdersWrapper /> },
                    { path: "/LabOrders", element: <LabOrdersWrapper /> },
                    { path: "/SalesAnalytics", element: <SalesAnalyticsWrapper /> },
                    { path: "/OrderSuccess", element: <OrderSuccessWrapper /> },
                    // Admin-only routes  
                    {
                        element: <AdminRoute />,
                        children: [
                            { path: "/AdminPanel", element: <AdminPanelWrapper /> },
                            { path: "/admin/user/:id", element: <UserDetailsWrapper /> },
                            { path: "/CRMPanel", element: <Navigate to="/CRMPanel/outbound" replace /> },
                            { path: "/CRMPanel/outbound", element: <CRMPanelWrapper /> },
                            { path: "/CRMPanel/inbound", element: <CRMInboundWrapper /> },
                        ],
                    },
                ],
            },

            // 404
            { path: "*", element: <NotFoundPage /> },
        ],
    },
]);

function App() {
    return (
        <AppStateProvider>
            <LicenseProvider>
                <LocationProvider>
                    <CoaJobsProvider>
                        <ErrorBoundary>
                            <RouterProvider router={router} />
                        </ErrorBoundary>
                    </CoaJobsProvider>
                </LocationProvider>
            </LicenseProvider>
        </AppStateProvider>
    );
}

export default App;